import { Box } from '@mui/material';
import { ICOM_CARDS } from '../../content';
import { gridGapSx } from '../../theme';
import { ListCard } from '../ListCard';
import type { IcomCardsProps } from './types';

/**
 * The four cards that say what a phase or process takes in, puts out, is
 * constrained by, and is staffed with.
 *
 * **Two columns, filled down rather than across** — see `ICOM_CARDS`, which sets
 * the order and says why.
 *
 * **One grid with explicit placement, not two flex stacks.** Two stacks give
 * column-major order but size each card to its own content, so the four end up
 * at four heights and the columns stop lining up; `1fr` rows make every row the
 * height of its tallest card.
 *
 * On mobile the placement is dropped — source order down one column is already
 * the order the two columns read down.
 *
 * **A card with no items renders nothing** and its cell is simply empty. That is
 * the common case rather than the exception — tens of pages carry no Controls +
 * Support, and the exact count moves every time the content does, so the
 * scheduler's `absent` report is where to read it.
 */
export const IcomCards = ({ content }: IcomCardsProps) => {
  const present = ICOM_CARDS.filter((card) => (content[card.field] ?? []).length > 0);

  if (present.length === 0) {
    return null;
  }

  const leftColumn = present.filter((card) => card.column === 'left');
  const rightColumn = present.filter((card) => card.column === 'right');
  const rowCount = Math.max(leftColumn.length, rightColumn.length);

  /** A card's row within its own column, which is what keeps the fill order down. */
  const rowOf = (field: string, column: 'left' | 'right'): number =>
    (column === 'left' ? leftColumn : rightColumn).findIndex((card) => card.field === field) + 1;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        gridTemplateRows: { xs: 'auto', sm: `repeat(${String(rowCount)}, 1fr)` },
        ...gridGapSx,
      }}
    >
      {present.map((card) => (
        <Box
          key={card.field}
          sx={{
            gridColumn: { xs: 'auto', sm: card.column === 'left' ? 1 : 2 },
            gridRow: { xs: 'auto', sm: rowOf(card.field, card.column) },
          }}
        >
          <ListCard
            heading={card.heading}
            caption={card.caption}
            items={content[card.field] ?? []}
          />
        </Box>
      ))}
    </Box>
  );
};
